/**
 * shopifyLineItemMatch — Shopify-Bestellposition ↔ FieldConnect-Produkt.
 *
 * Hintergrund: Der Katalog-Import (PIM) schreibt die Shopify-SKUs in
 * `product_variants.sku` und `products.sku`. Der Bestell-Sync hat bisher nur
 * `products.artikelnummer` verglichen — fast keine Position bekam deshalb eine
 * product_id, Bestand/Historie/Lagerort blieben leer.
 *
 * Reihenfolge der Zuordnung (erster Treffer gewinnt):
 *   1. SKU  ↔ product_variants.sku         (eine SKU je Packgröße/Variante)
 *   2. SKU  ↔ products.sku
 *   3. SKU  ↔ products.artikelnummer       (Altbestände)
 *   4. Shopify-Varianten-ID ↔ product_variants.shopify_variant_id
 *      (fängt SKU-Umbenennungen in Shopify ab)
 *
 * `variant_value` für die Lager-RPCs: Die Stock-Engine kennt NUR die alte
 * JSON-Spalte `products.variants[].value`. Ein unbekannter Wert löst dort
 * `variant_not_found` aus und rollt die ganze Reservierung zurück. Deshalb
 * wird der Titel nur übergeben, wenn er in der JSON-Spalte existiert — sonst
 * null (Buchung auf Produktebene). Der Anzeigetitel wandert nach `variant_label`.
 */

import { parseLegacyVariants } from '@/lib/productVariantOptions';

const DEFAULT_UNIT = 'Stück';
const DEFAULT_TITLES = new Set(['default title', 'standard', 'default']);

/** SKU-Normalisierung — dieselbe Regel wie im Katalog-Sync (trim + lowercase). */
export function normSku(sku) {
  return String(sku ?? '').trim().toLowerCase();
}

/**
 * Baut die Nachschlage-Strukturen einmal pro Sync-Lauf.
 * @param {{ products: Array<object>, variants: Array<object> }} src
 */
export function buildProductIndex({ products = [], variants = [] } = {}) {
  const productById = new Map(products.map((p) => [p.id, p]));
  const variantBySku = new Map();
  const variantByShopifyId = new Map();
  const productBySku = new Map();
  const productByArtikelnummer = new Map();

  // Aktive Varianten gewinnen bei gleicher SKU; deaktivierte bleiben als
  // Fallback, damit alte Bestellungen weiterhin verknüpft werden können.
  const sorted = [...variants].sort((a, b) => Number(b.aktiv !== false) - Number(a.aktiv !== false));
  sorted.forEach((v) => {
    if (!v?.product_id || !productById.has(v.product_id)) return;
    const k = normSku(v.sku);
    if (k && !variantBySku.has(k)) variantBySku.set(k, v);
    if (v.shopify_variant_id && !variantByShopifyId.has(v.shopify_variant_id)) {
      variantByShopifyId.set(v.shopify_variant_id, v);
    }
  });

  products.forEach((p) => {
    const sku = normSku(p.sku);
    if (sku && !productBySku.has(sku)) productBySku.set(sku, p);
    const art = normSku(p.artikelnummer);
    if (art && !productByArtikelnummer.has(art)) productByArtikelnummer.set(art, p);
  });

  return { productById, variantBySku, variantByShopifyId, productBySku, productByArtikelnummer };
}

function cleanTitle(title) {
  const t = String(title ?? '').trim();
  if (!t || DEFAULT_TITLES.has(t.toLowerCase())) return null;
  return t;
}

/**
 * Ordnet eine Shopify-Position (GraphQL LineItem) einem FC-Produkt zu.
 * @param {{ sku?: string, variantTitle?: string, variant?: { id?: string } }} li
 * @param {ReturnType<typeof buildProductIndex>} index
 * @returns {{ product_id: string|null, product: object|null, variant: object|null,
 *             unit: string, matched_by: string|null }}
 */
export function matchLineItem(li, index) {
  const sku = normSku(li?.sku);
  let variant = null;
  let product = null;
  let matchedBy = null;

  if (sku && index.variantBySku.has(sku)) {
    variant = index.variantBySku.get(sku);
    product = index.productById.get(variant.product_id) || null;
    matchedBy = 'variant_sku';
  } else if (sku && index.productBySku.has(sku)) {
    product = index.productBySku.get(sku);
    matchedBy = 'product_sku';
  } else if (sku && index.productByArtikelnummer.has(sku)) {
    product = index.productByArtikelnummer.get(sku);
    matchedBy = 'artikelnummer';
  } else if (li?.variant?.id && index.variantByShopifyId.has(li.variant.id)) {
    variant = index.variantByShopifyId.get(li.variant.id);
    product = index.productById.get(variant.product_id) || null;
    matchedBy = 'shopify_variant_id';
  }

  if (!product) {
    return { product_id: null, product: null, variant: null, unit: DEFAULT_UNIT, matched_by: null };
  }
  return {
    product_id: product.id,
    product,
    variant,
    unit: product.einheit || DEFAULT_UNIT,
    matched_by: matchedBy,
  };
}

/**
 * Lager-sicherer Variantenwert: nur ein Wert, den `products.variants` kennt.
 * @param {object|null} product
 * @param {string|null} shopifyTitle   Shopify variantTitle
 * @param {string|null} fcVariantTitle product_variants.title
 * @returns {string|null}
 */
export function stockVariantValue(product, shopifyTitle, fcVariantTitle) {
  if (!product) return null;
  const known = new Set(
    parseLegacyVariants(product.variants)
      .map((l) => (typeof l?.value === 'string' ? l.value.trim() : ''))
      .filter(Boolean),
  );
  if (known.size === 0) return null;
  for (const candidate of [cleanTitle(shopifyTitle), cleanTitle(fcVariantTitle)]) {
    if (candidate && known.has(candidate)) return candidate;
  }
  return null;
}

/** Anzeigetitel der Variante (Shopify-Titel, sonst FC-Titel). */
export function variantLabel(shopifyTitle, fcVariantTitle) {
  return cleanTitle(shopifyTitle) || cleanTitle(fcVariantTitle);
}

/**
 * Nachträgliche Zuordnung für bereits importierte Bestellungen: Nur Positionen
 * OHNE product_id werden ergänzt (product_id, variant_value, unit) — Menge,
 * Preis und Name der gespeicherten Bestellung bleiben unverändert.
 *
 * @param {Array<object>} stored  Positionen aus der Datenbank
 * @param {Array<object>} fresh   frisch gemappte Positionen (gleiche Reihenfolge)
 * @returns {{ items: Array<object>, newlyMatched: Array<object> }}
 */
export function mergeMatchedItems(stored, fresh) {
  const a = Array.isArray(stored) ? stored : [];
  const b = Array.isArray(fresh) ? fresh : [];
  if (a.length !== b.length) return { items: a, newlyMatched: [] };

  const newlyMatched = [];
  const items = a.map((item, i) => {
    const f = b[i];
    if (item.product_id || !f?.product_id) return item;
    if (normSku(item.product_sku) !== normSku(f.product_sku)) return item;
    const merged = {
      ...item,
      product_id: f.product_id,
      variant_value: f.variant_value ?? null,
      variant_label: item.variant_label ?? f.variant_label ?? null,
      unit: f.unit || item.unit,
    };
    newlyMatched.push(merged);
    return merged;
  });
  return { items, newlyMatched };
}
